import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PublicClientApplication, AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { UserService, UserModel } from './user.service';
import { LoggerService } from '../core/services/logger.service';

export interface SSOUser {
  name?: string;
  email?: string;
  id?: string;
  // Additional user details from API
  userId?: number;
  firstName?: string;
  lastName?: string;
  roleId?: number;
  roleName?: string;
  userTypeId?: number;
  userTypeName?: string;
  isActive?: boolean;
  entraUserId?: string;
  colorCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SSOService {
  private msalInstance: PublicClientApplication | null = null;
  private userSubject = new BehaviorSubject<SSOUser | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private initializationPromise: Promise<void> | null = null;
  private isLoggingOut = false; // Flag to prevent auto-login after logout
  private isApiUserDataLoaded = false; // Flag to prevent duplicate GetUserByEmail calls
  
  // Token caching
  private cachedToken: string | null = null;
  private tokenExpiryTime: number = 0;
  private tokenRefreshPromise: Promise<string | null> | null = null;

  public user$ = this.userSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private getLoginRedirectUrl(): string {
    const normalizedAppName = (environment.appName || '').replace(/^\/+|\/+$/g, '');
    const appPath = normalizedAppName ? `/${normalizedAppName}` : '';
    return `${window.location.origin}${appPath}/login`;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private userService: UserService,
    private logger: LoggerService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializationPromise = this.initializeMsal();
    }
  }

  private async initializeMsal(): Promise<void> {
    try {
      const msalConfig = {
        auth: {
          clientId: environment.clientId,
          authority: `https://login.microsoftonline.com/${environment.tenantId}`,
          redirectUri: environment.redirectUri,
          navigateToLoginRequestUrl: false
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false
        }
      };

      this.msalInstance = new PublicClientApplication(msalConfig);

      // Initialize MSAL before calling any other methods
      await this.msalInstance.initialize();

      // Handle redirect promise after initialization
      try {
          
        const response = await this.msalInstance.handleRedirectPromise();
        if (response) {
         
          await this.handleAuthenticationResult(response);
        } else if (!this.isLoggingOut) {
          // Check if user is already signed in (only if not in logout process)
        
          await this.checkExistingSession();
        } else {
          this.logger.info('SSOService: Logout in progress, skipping session check');
        }
      } catch (redirectError) {
        this.logger.error('SSOService: Error handling redirect promise:', redirectError);
        // Don't fail initialization if redirect handling fails, just check existing session
        if (!this.isLoggingOut) {
          try {
            this.logger.info('SSOService: Redirect failed, checking existing session as fallback...');
            await this.checkExistingSession();
          } catch (sessionError) {
            this.logger.error('SSOService: Session check also failed:', sessionError);
            // Continue with initialization even if session check fails
          }
        } else {
          this.logger.info('SSOService: Logout in progress, skipping session check fallback');
        }
      }
    } catch (error) {
      this.logger.error('MSAL initialization error:', error);
      this.msalInstance = null;
      // Reset initialization promise so it can be retried
      this.initializationPromise = null;
      throw error; // Re-throw to indicate initialization failed
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('MSAL not available - not in browser environment');
    }

    // Wait for existing initialization if in progress
    if (this.initializationPromise) {
      await this.initializationPromise;
    }

    // Check if MSAL instance is properly initialized
    if (!this.msalInstance) {
      this.logger.warn('MSAL instance not found, attempting to re-initialize...');
      this.initializationPromise = this.initializeMsal();
      await this.initializationPromise;
    }

    // Final check - throw error if still not ready
    if (!this.msalInstance) {
      throw new Error('MSAL initialization failed - instance is null');
    }

    // Verify MSAL is properly initialized
    try {
      const config = this.msalInstance.getConfiguration();
      if (!config) {
        throw new Error('MSAL instance not properly configured');
      }
    } catch (error) {
      this.logger.error('MSAL configuration check failed:', error);
      throw new Error('MSAL instance not properly initialized');
    }
  }

  async loginWithRedirect(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.warn('MSAL not available - not in browser environment');
      return;
    }

    try {
      // Ensure MSAL is initialized
      await this.ensureInitialized();
    } catch (error) {
      this.logger.error('Failed to initialize MSAL for redirect login:', error);
      throw error;
    }

    // After ensureInitialized, msalInstance is guaranteed to be non-null
    if (!this.msalInstance) {
      throw new Error('MSAL instance is null after initialization');
    }

    const loginRequest = {
      scopes: [`${environment.clientId}/.default`],
      prompt: 'select_account'
    };

    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      this.logger.error('Login redirect error:', error);
      throw error;
    }
  }

  async loginWithPopup(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.warn('MSAL not available - not in browser environment');
      return;
    }

    try {
      // Ensure MSAL is initialized
      await this.ensureInitialized();
    } catch (error) {
      this.logger.error('Failed to initialize MSAL for popup login:', error);
      throw error;
    }

    // After ensureInitialized, msalInstance is guaranteed to be non-null
    if (!this.msalInstance) {
      throw new Error('MSAL instance is null after initialization');
    }

    const loginRequest = {
      scopes: [`${environment.clientId}/.default`],
      prompt: 'select_account'
    };
    try {
      const response = await this.msalInstance.loginPopup(loginRequest);
      await this.handleAuthenticationResult(response);
    } catch (error) {
      this.logger.error('Login popup error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.logger.info('SSOService: Starting logout process...');
    this.isLoggingOut = true;
    this.isApiUserDataLoaded = false; // Reset flag for next login
    
    // Clear user data immediately
    this.clearUserData();

    try {
      // Ensure MSAL is initialized
      await this.ensureInitialized();
      
      if (this.msalInstance) {
        // Clear all MSAL accounts and cache
        const accounts = this.msalInstance.getAllAccounts();
        this.logger.info('SSOService: Clearing', accounts.length, 'MSAL accounts');
        
        if (accounts.length > 0) {
          // Clear MSAL cache using the correct API
          try {
            this.logger.info('SSOService: Clearing MSAL token cache');
            await this.msalInstance.clearCache();
          } catch (error) {
            this.logger.warn('Error clearing MSAL cache:', error);
          }
          
          // Perform MSAL logout redirect
          const logoutRequest = {
            account: accounts[0],
            postLogoutRedirectUri: this.getLoginRedirectUrl()
          };
          
          try {
            this.logger.info('SSOService: Performing MSAL logout redirect');
            await this.msalInstance.logoutRedirect(logoutRequest);
            return; // Don't continue, redirect will handle navigation
          } catch (logoutError) {
            this.logger.error('MSAL logout redirect failed:', logoutError);
          }
        }
      }
    } catch (error) {
      this.logger.error('Error during MSAL logout:', error);
    }
    
    // Fallback: manual redirect if MSAL logout fails
    this.logger.info('SSOService: Fallback - redirecting manually to login');
    this.isLoggingOut = false;
    window.location.href = this.getLoginRedirectUrl();
  }

  /**
   * Get cached token if valid, otherwise return null
   */
  getCachedToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    // Check if we have a valid cached token
    if (this.cachedToken && Date.now() < this.tokenExpiryTime) {
      this.logger.info('SSOService: Using cached token (expires in', Math.round((this.tokenExpiryTime - Date.now()) / 1000), 'seconds)');
      return this.cachedToken;
    }

    return null;
  }

  /**
   * Get access token with efficient caching - only makes network calls when needed
   */
  async getAccessToken(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.warn('SSOService: Not in browser environment, cannot get access token');
      return null;
    }

    // Check cached token first
    const cachedToken = this.getCachedToken();
    if (cachedToken) {
      return cachedToken;
    }

    // If already refreshing token, wait for that promise
    if (this.tokenRefreshPromise) {
      this.logger.info('SSOService: Token refresh already in progress, waiting...');
      return await this.tokenRefreshPromise;
    }

    // Start token refresh
    this.tokenRefreshPromise = this.refreshAccessToken();
    
    try {
      const token = await this.tokenRefreshPromise;
      return token;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  /**
   * Internal method to refresh access token from MSAL
   */
  private async refreshAccessToken(): Promise<string | null> {
    this.logger.info('SSOService: Refreshing access token from MSAL...');
    
    try {
      await this.ensureInitialized();
    } catch (error) {
      this.logger.error('SSOService: Failed to initialize MSAL for token acquisition:', error);
      this.clearTokenCache();
      this.clearUserData();
      return null;
    }

    if (!this.msalInstance) {
      this.logger.error('SSOService: MSAL instance is null after initialization');
      this.clearTokenCache();
      this.clearUserData();
      return null;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      this.logger.warn('SSOService: No accounts found. User may not be logged in.');
      this.clearTokenCache();
      this.clearUserData();
      return null;
    }

    const silentRequest: SilentRequest = {
      scopes: [`${environment.clientId}/.default`],
      account: accounts[0]
    };

    try {
      const response = await this.msalInstance!.acquireTokenSilent(silentRequest);
      
      if (!response.accessToken) {
        this.logger.error('SSOService: Token response is empty');
        this.clearTokenCache();
        this.clearUserData();
        return null;
      }

      // Cache the token with expiry time
      this.cacheToken(response.accessToken, response.expiresOn);
      
      this.logger.info('SSOService: Token refreshed successfully (expires:', response.expiresOn, ')');
      return response.accessToken;

    } catch (error: any) {
      this.logger.error('SSOService: Silent token acquisition failed:', error?.message || error);

      // If silent acquisition fails due to interaction required, try popup
      if (error?.name === 'InteractionRequiredAuthError' || error?.message?.includes('interaction_required')) {
        this.logger.info('SSOService: Requesting user consent via popup...');
        try {
          const popupResponse = await this.msalInstance!.acquireTokenPopup(silentRequest);
          if (!popupResponse.accessToken) {
            this.logger.error('SSOService: Popup token response is empty');
            this.clearTokenCache();
            this.clearUserData();
            return null;
          }
          
          // Cache the token from popup
          this.cacheToken(popupResponse.accessToken, popupResponse.expiresOn);
          return popupResponse.accessToken;
        } catch (popupError) {
          this.logger.error('SSOService: Popup token acquisition failed:', popupError);
          this.clearTokenCache();
          this.clearUserData();
          return null;
        }
      }

      // For other errors, clear authentication state
      this.logger.error('SSOService: Clearing authentication state due to token acquisition failure');
      this.clearTokenCache();
      this.clearUserData();
      return null;
    }
  }

  /**
   * Cache token with expiry time (with 5 minute buffer to avoid edge cases)
   */
  private cacheToken(token: string, expiresOn: Date | null): void {
    this.cachedToken = token;
    
    if (expiresOn) {
      // Add 5 minute buffer before expiry to ensure we refresh before it actually expires
      this.tokenExpiryTime = expiresOn.getTime() - (5 * 60 * 1000);
    } else {
      // Default to 1 hour if no expiry provided
      this.tokenExpiryTime = Date.now() + (60 * 60 * 1000);
    }
    
    this.logger.info('SSOService: Token cached until', new Date(this.tokenExpiryTime));
  }

  /**
   * Clear cached token
   */
  private clearTokenCache(): void {
    this.cachedToken = null;
    this.tokenExpiryTime = 0;
    this.logger.info('SSOService: Token cache cleared');
  }

  getCurrentUser(): SSOUser | null {
    return this.userSubject.value;
  }

  /**
   * Returns a canonical display name for UI usage.
   * Preference order: firstName+lastName -> normalized Azure display name -> email -> fallback.
   */
  getPreferredUserName(user?: SSOUser | null): string {
    const resolvedUser = user ?? this.userSubject.value;
    if (!resolvedUser) {
      return 'Unknown User';
    }

    const firstName = (resolvedUser.firstName || '').trim();
    const lastName = (resolvedUser.lastName || '').trim();
    if (firstName && lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    const normalizedAzureName = this.normalizeAzureDisplayName(resolvedUser.name);
    if (normalizedAzureName) {
      return normalizedAzureName;
    }

    return (resolvedUser.email || '').trim() || 'Unknown User';
  }

  /**
   * Ensures firstName/lastName are loaded from user profile API.
   * Useful for UI paths that require stable name formatting.
   */
  async ensureCurrentUserProfileLoaded(): Promise<SSOUser | null> {
    const currentUser = this.userSubject.value;
    if (!currentUser) {
      return null;
    }

    const firstName = (currentUser.firstName || '').trim();
    const lastName = (currentUser.lastName || '').trim();
    if (firstName && lastName) {
      return currentUser;
    }

    const email = (currentUser.email || '').trim();
    if (!email) {
      return currentUser;
    }

    try {
      const apiUser = await firstValueFrom(this.userService.getUserByEmail(email));
      const completeUser: SSOUser = {
        ...currentUser,
        userId: apiUser.userId,
        firstName: apiUser.firstName,
        lastName: apiUser.lastName,
        roleId: apiUser.roleId,
        roleName: apiUser.roleName,
        userTypeId: apiUser.userTypeId,
        userTypeName: apiUser.userTypeName,
        isActive: apiUser.isActive,
        colorCode: apiUser.colorCode
      };

      this.userSubject.next(completeUser);
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem('sso_user', JSON.stringify(completeUser));
      }

      return completeUser;
    } catch (error) {
      this.logger.warn('SSOService: Failed to ensure profile during UI name resolution:', error);
      return currentUser;
    }
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Get complete user details including role information
   */
  getUserDetails(): SSOUser | null {
    const user = this.userSubject.value;
    if (user && user.userId) {
      return user;
    }
    return null;
  }

  /**
   * Check if the current user has a specific role
   */
  hasRole(roleName: string): boolean {
    const user = this.getCurrentUser();
    return (user?.roleName || '').trim().toLowerCase() === (roleName || '').trim().toLowerCase();
  }

  /**
   * Check if the current user is active
   */
  isUserActive(): boolean {
    const user = this.getCurrentUser();
    return user?.isActive === true;
  }

  private async handleAuthenticationResult(result: AuthenticationResult): Promise<void> {
    if (result && result.account) {
      const basicUser: SSOUser = {
        name: result.account.name || '',
        email: result.account.username || '',
        id: result.account.homeAccountId || ''
      };

      this.logger.info('SSOService: Handling authentication result for user:', basicUser);
      
      // Set basic authentication state first
      this.userSubject.next(basicUser);
      this.isAuthenticatedSubject.next(true);

      // Fetch complete user details from API
      if (basicUser.email) {
        try {
          this.logger.info('SSOService: Fetching user details from API for email:', basicUser.email);
          
          // Call getUserByEmail API to get complete user information
          this.userService.getUserByEmail(basicUser.email).subscribe({
            next: (apiUser: UserModel) => {
              this.logger.info('SSOService: Retrieved user details from API:', apiUser);
              
              // Merge SSO data with API data
              const completeUser: SSOUser = {
                ...basicUser,
                userId: apiUser.userId,
                firstName: apiUser.firstName,
                lastName: apiUser.lastName,
                roleId: apiUser.roleId,
                roleName: apiUser.roleName,
                userTypeId: apiUser.userTypeId,
                userTypeName: apiUser.userTypeName,
                isActive: apiUser.isActive,
                colorCode: apiUser.colorCode
              };

              // Update user data with complete information
              this.userSubject.next(completeUser);

              // Store complete user info in localStorage
              if (isPlatformBrowser(this.platformId)) {
                localStorage.setItem('sso_user', JSON.stringify(completeUser));
                localStorage.setItem('sso_authenticated', 'true');
              }

              this.logger.info('SSOService: Authentication complete with full user details:', completeUser);
            },
            error: (error) => {
              this.logger.error('SSOService: Failed to fetch user details from API:', error);
              
              // Still proceed with basic SSO info if API call fails
              if (isPlatformBrowser(this.platformId)) {
                localStorage.setItem('sso_user', JSON.stringify(basicUser));
                localStorage.setItem('sso_authenticated', 'true');
              }
              
              this.logger.info('SSOService: Authentication successful with basic SSO info only:', basicUser);
            }
          });
        } catch (error) {
          this.logger.error('SSOService: Error calling getUserByEmail API:', error);
          
          // Fallback to basic user info
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('sso_user', JSON.stringify(basicUser));
            localStorage.setItem('sso_authenticated', 'true');
          }
        }
      } else {
        this.logger.warn('SSOService: No email found in SSO result, using basic info only');
        
        // Store basic user info if no email
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('sso_user', JSON.stringify(basicUser));
          localStorage.setItem('sso_authenticated', 'true');
        }
      }
    }
  }

  private async checkExistingSession(): Promise<void> {
    if (!this.msalInstance || !isPlatformBrowser(this.platformId) || this.isLoggingOut) {
      return;
    }

    try {
      // Ensure MSAL is initialized
      if (!this.msalInstance.getConfiguration()) {
        await this.msalInstance.initialize();
      }

      const accounts = this.msalInstance.getAllAccounts();
      this.logger.info('SSOService: Checking existing session, found accounts:', accounts.length);
      if (accounts.length > 0) {
        const account = accounts[0];
        const basicUser: SSOUser = {
          name: account.name || '',
          email: account.username || '',
          id: account.homeAccountId || ''
        };

        // Set basic authentication state first
        this.userSubject.next(basicUser);
        this.isAuthenticatedSubject.next(true);

        // Check if we already have complete user data from localStorage
        const storedUser = JSON.parse(localStorage.getItem('sso_user') || '{}');
        if (storedUser.userId) {
          // We already have complete user data, no need to call API again
          this.logger.info('SSOService: Using cached complete user data from localStorage');
          this.userSubject.next(storedUser);
          this.isApiUserDataLoaded = true;
        } else if (basicUser.email && !this.isApiUserDataLoaded) {
          // Only fetch from API if we don't have complete data yet
          this.logger.info('SSOService: Fetching complete user data from API for existing session');
          this.isApiUserDataLoaded = true;
          
          this.userService.getUserByEmail(basicUser.email).subscribe({
            next: (apiUser: UserModel) => {
              this.logger.info('SSOService: Updated existing session with API user details');
              
              const completeUser: SSOUser = {
                ...basicUser,
                userId: apiUser.userId,
                firstName: apiUser.firstName,
                lastName: apiUser.lastName,
                roleId: apiUser.roleId,
                roleName: apiUser.roleName,
                userTypeId: apiUser.userTypeId,
                userTypeName: apiUser.userTypeName,
                isActive: apiUser.isActive,
                colorCode: apiUser.colorCode
              };

              this.userSubject.next(completeUser);
              localStorage.setItem('sso_user', JSON.stringify(completeUser));
            },
            error: (error) => {
              this.logger.warn('SSOService: Could not fetch API user details for existing session:', error);
              localStorage.setItem('sso_user', JSON.stringify(basicUser));
              this.isApiUserDataLoaded = false; // Reset flag to allow retry
            }
          });
        } else {
          localStorage.setItem('sso_user', JSON.stringify(basicUser));
        }

        localStorage.setItem('sso_authenticated', 'true');
        this.logger.info('Existing SSO session found and activated');
      } else {
        // Try to restore from localStorage 
        await this.restoreFromLocalStorage();
      }
    } catch (error) {
      this.logger.error('Error checking existing session:', error);
      await this.restoreFromLocalStorage();
    }
  }

  private async restoreFromLocalStorage(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.isLoggingOut) {
      return;
    }

    try {
      const storedUser = localStorage.getItem('sso_user');
      const isAuthenticated = localStorage.getItem('sso_authenticated') === 'true';

      if (storedUser && isAuthenticated) {
        const user: SSOUser = JSON.parse(storedUser);
        
        // Restore the user session from localStorage
        // Token validation will happen when actually needed (in getAccessToken)
        this.userSubject.next(user);
        this.isAuthenticatedSubject.next(true);
        this.logger.info('Restored SSO session from localStorage');
      } else {
        // No valid stored session
        this.logger.info('No valid stored SSO session found');
        this.clearUserData();
      }
    } catch (error) {
      this.logger.error('Error restoring SSO session from localStorage:', error);
      this.clearUserData();
    }
  }

  private clearUserData(): void {
    this.logger.info('SSOService: Clearing all user data and authentication state');
    
    // Clear internal state
    this.userSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    
    // Clear cached token
    this.clearTokenCache();

    if (isPlatformBrowser(this.platformId)) {
      // Clear all SSO-related localStorage items
      localStorage.removeItem('sso_user');
      localStorage.removeItem('sso_authenticated');
      
      // Clear traditional token as well
      localStorage.removeItem('token');
      
      // Clear any other auth-related items
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('msal.') || key.startsWith('sso_') || key === 'token')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        this.logger.info('SSOService: Removing localStorage key:', key);
        localStorage.removeItem(key);
      });
    }
  }

  /**
   * Check if MSAL is ready for use
   */
  async isReady(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      await this.ensureInitialized();
      return true; // If ensureInitialized succeeds, MSAL is ready
    } catch (error) {
      this.logger.error('MSAL readiness check failed:', error);
      return false;
    }
  }

  /**
   * Force a fresh authentication attempt - useful when tokens are invalid
   */
  async forceReAuthentication(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    this.logger.info('SSOService: Forcing re-authentication...');
    this.clearUserData();

    try {
      await this.loginWithPopup();
      return this.isAuthenticated();
    } catch (error) {
      this.logger.error('Force re-authentication failed:', error);
      return false;
    }
  }

  /**
   * Handle authentication redirect callback
   */
  async handleAuthCallback(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      await this.ensureInitialized();
      
      if (!this.msalInstance) {
        return false;
      }

      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        await this.handleAuthenticationResult(response);
        return true;
      }
      return false;
    } catch (error) {
      this.logger.error('Error handling auth callback:', error);
      return false;
    }
  }

  /**
   * Reset logout flag - useful for error recovery
   */
  resetLogoutFlag(): void {
    this.isLoggingOut = false;
  }

  /**
   * Initialize and wait for authentication state to be determined
   */
  async waitForInitialization(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      await this.ensureInitialized();

      // Wait a brief moment for authentication state to be determined
      return new Promise((resolve) => {
        // If already authenticated, validate the token
        if (this.isAuthenticated()) {
          this.getAccessToken().then(token => {
            if (token) {
              resolve(true);
            } else {
              this.logger.warn('Authentication state invalid: no access token available');
              this.clearUserData();
              resolve(false);
            }
          }).catch(error => {
            this.logger.error('Token validation failed during initialization:', error);
            this.clearUserData();
            resolve(false);
          });
          return;
        }

        // Check after a short delay to allow for session restoration
        setTimeout(() => {
          if (this.isAuthenticated()) {
            // Validate token after delay
            this.getAccessToken().then(token => {
              if (token) {
                resolve(true);
              } else {
                this.clearUserData();
                resolve(false);
              }
            }).catch(() => {
              this.clearUserData();
              resolve(false);
            });
          } else {
            resolve(false);
          }
        }, 1000);
      });
    } catch (error) {
      this.logger.error('SSO initialization failed:', error);
      return false;
    }
  }

  /**
   * Check if the current user has complete data from GetUserByEmail API
   */
  hasCompleteUserData(): boolean {
    const currentUser = this.userSubject.value;
    return !!(currentUser && currentUser.userId && currentUser.firstName && currentUser.lastName);
  }

  /**
   * Get the current user data synchronously
   */
  getCurrentUserData(): SSOUser | null {
    return this.userSubject.value;
  }

  private normalizeAzureDisplayName(name?: string): string {
    const raw = (name || '').trim();
    if (!raw) {
      return '';
    }

    // Azure display name is often "Last, First". Normalize to "First Last" for UI consistency.
    if (raw.includes(',')) {
      const parts = raw.split(',').map(part => part.trim()).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts.slice(1).join(' ')} ${parts[0]}`.trim();
      }
    }

    return raw;
  }
}

